import { Sparkles } from 'lucide-react'
import { AnimatedKpiValue } from '@/components/ui/AnimatedKpiValue'
import type { ClosingForecast } from '@/components/Dashboard/pipelineTab.utils'
import {
  formatDashboardCurrency,
  formatDashboardNumber,
} from '@/utils/crmValuesDisplay'
import { formatCurrencyCompact } from '@/utils/format-currency'

type Props = {
  forecast: ClosingForecast
  valuesVisible?: boolean
  animationDelay?: number
}

export function PipelineForecastHeroCard({
  forecast,
  valuesVisible = false,
  animationDelay = 0,
}: Props) {
  const accent = { bg: '#ede9fe', text: '#7c3aed', ring: '#c4b5fd' }
  const useCompact = valuesVisible && forecast.forecastValue >= 100_000

  return (
    <article className='relative flex h-full min-h-[260px] flex-col overflow-hidden rounded-2xl border border-[var(--border)] bg-[var(--bg-card)] p-5 shadow-sm'>
      <div
        className='pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full opacity-40 blur-2xl'
        style={{ backgroundColor: accent.ring }}
        aria-hidden
      />

      <div className='flex items-start justify-between gap-3'>
        <div className='min-w-0'>
          <p className='text-sm font-medium text-text-muted'>Previsão de fechamento</p>
          <div className='mt-2'>
            {valuesVisible ? (
              <AnimatedKpiValue
                numericValue={forecast.forecastValue}
                displayValue={
                  useCompact
                    ? formatCurrencyCompact(forecast.forecastValue)
                    : formatDashboardCurrency(forecast.forecastValue, true)
                }
                format='currency'
                compactCurrency={useCompact}
                className='text-3xl font-bold tabular-nums tracking-tight text-text-primary sm:text-4xl'
                delay={animationDelay}
              />
            ) : (
              <span className='text-3xl font-bold text-text-primary sm:text-4xl'>••••</span>
            )}
          </div>
          <p className='mt-2 text-sm text-text-muted'>
            {formatDashboardNumber(forecast.forecastCount, valuesVisible)} deals desde proposta
            enviada · ponderado por etapa
          </p>
        </div>
        <span
          className='flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ring-1'
          style={{
            backgroundColor: accent.bg,
            color: accent.text,
            boxShadow: `inset 0 0 0 1px ${accent.ring}`,
          }}
          aria-hidden
        >
          <Sparkles size={18} />
        </span>
      </div>

      <div className='mt-auto space-y-2 border-t border-[var(--border)] pt-4'>
        <div className='flex items-center justify-between text-sm'>
          <span className='text-text-secondary'>Propostas enviadas</span>
          <span className='font-semibold tabular-nums text-text-primary'>
            {formatDashboardNumber(forecast.proposalsSentCount, valuesVisible)} deals ·{' '}
            {formatDashboardCurrency(forecast.proposalsSentForecast, valuesVisible)} prev.
          </span>
        </div>
        <div className='flex items-center justify-between text-sm'>
          <span className='text-text-secondary'>Valor bruto no funil</span>
          <span className='font-semibold tabular-nums text-text-primary'>
            {formatDashboardCurrency(forecast.rawValue, valuesVisible)}
          </span>
        </div>
      </div>
    </article>
  )
}
